import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../utils/axios';
import { createAuthedThunk } from '../authedThunk';

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
};
const fetchDataInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  data: null,
};

const initialState = {
  crmConnected: '',
  aircallConnected: false,
  zoomS2SConnected: false,
  gmailOAuthUrl: { ...fetchDataInitialState },
  unipileGmailOAuthUrl: { ...fetchDataInitialState },
  unipileOutlookOAuthUrl: { ...fetchDataInitialState },
  unipileGoogleCalendarOAuthUrl: { ...fetchDataInitialState },
  unipileOutlookCalendarOAuthUrl: { ...fetchDataInitialState },
  unipileLinkedInOAuthUrl: { ...fetchDataInitialState },
  validateGMailCreds: { ...mutationInitialState },
  gmailOAuthCallback: { ...mutationInitialState },
  googleOAuthUrl: { ...fetchDataInitialState },
  googleOAuthCallback: { ...mutationInitialState },
  outlookOAuthUrl: { ...fetchDataInitialState },
  outlookCalendarOAuthUrl: { ...fetchDataInitialState },
  outlookOAuthCallback: { ...mutationInitialState },
  outlookCalendarOAuthCallback: { ...mutationInitialState },
  pipedriveOAuthUrl: { ...fetchDataInitialState },
  pipedriveOAuthCallback: { ...mutationInitialState },
  zohoOAuthUrl: { ...fetchDataInitialState },
  zohoOAuthCallback: { ...mutationInitialState },
  zoomWebhookUrl: { ...fetchDataInitialState },
  zoomOAuthUrl: { ...fetchDataInitialState },
  zoomOAuthCallback: { ...mutationInitialState },
  slackOAuthUrl: { ...fetchDataInitialState },
  slackOAuthCallback: { ...mutationInitialState },
  slackChannels: { ...fetchDataInitialState },
  slackNotificationChannels: { ...fetchDataInitialState },
  updateSlackNotificationChannels: { ...mutationInitialState },
  slackUserMapping: { ...fetchDataInitialState },
  integrateHubspot: { ...mutationInitialState },
  oAuthCreds: { ...fetchDataInitialState },
  integrationKey: { ...fetchDataInitialState },
  syncRequestStatus: { ...fetchDataInitialState },
  crmHubspotSync: { ...mutationInitialState },
  syncDataWithCrm: { ...mutationInitialState },
  syncDataWithHubspot: { ...mutationInitialState },
  syncCalls: { ...mutationInitialState },
  syncZoomCalls: { ...mutationInitialState },
  manuallyTriggerBotToJoin: { ...mutationInitialState },
  orgPreferences: { ...fetchDataInitialState },
  updateOrgPreferences: { ...mutationInitialState },
  connectIntegration: { ...mutationInitialState },
  disconnectIntegration: { ...mutationInitialState },
  updateIntegration: { ...mutationInitialState },
  userIntegrations: { ...fetchDataInitialState },
  orgIntegrations: { ...fetchDataInitialState },
  mcpOAuthLogin: { ...mutationInitialState },
  mcpOAuthConsent: { ...fetchDataInitialState },
  mcpOAuthConsentDecision: { ...mutationInitialState },
  mcpOAuthConnectionStatus: { ...fetchDataInitialState },
};

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const simpleThunk = createAuthedThunk;

/**
 * Builds a thunk that requests an OAuth authorization URL for one provider.
 *
 * Thirteen of these existed, differing only in a provider string and which
 * extra body fields they forwarded. They stay thirteen separate thunks on
 * purpose: each has its own state slot (`gmailOAuthUrl`, `zoomOAuthUrl`, ...)
 * wired through `addF`, so collapsing them into one parameterized thunk would
 * merge thirteen independent loading states - connecting Gmail would spin the
 * Zoom button. Only the duplicated plumbing is shared.
 *
 * @param {string} name action type prefix
 * @param {string} provider backend provider identifier
 * @param {(args: Object) => Object} [extraBody] additional body fields
 * @returns {import('@reduxjs/toolkit').AsyncThunk<any, any, {}>} the thunk
 */
const oauthUrlThunk = (name, provider, extraBody) =>
  simpleThunk(name, async (token, args) => {
    const r = await axios.post(
      '/us/integrations/oauth/url',
      { provider, ...(extraBody ? extraBody(args || {}) : {}) },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data?.url ?? r.data;
    throw new Error('Failed');
  });

/**
 * Builds a thunk that exchanges an OAuth callback for one provider.
 *
 * Same reasoning as {@link oauthUrlThunk}: separate thunks, shared plumbing.
 *
 * @param {string} name action type prefix
 * @param {string} provider backend provider identifier
 * @param {(args: Object) => Object} buildBody the provider's body fields
 * @returns {import('@reduxjs/toolkit').AsyncThunk<any, any, {}>} the thunk
 */
const oauthCallbackThunk = (name, provider, buildBody) =>
  simpleThunk(name, async (token, args) => {
    const r = await axios.post(
      '/us/integrations/oauth/callback',
      { provider, ...buildBody(args || {}) },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  });

export const getGmailOAuthURL = oauthUrlThunk(
  'integrations/getGmailOAuthURL',
  'GMAIL_OAUTH',
  ({ readWriteAccess }) => ({ readWriteAccess }),
);

export const getUnipileGmailOAuthURL = oauthUrlThunk(
  'integrations/getUnipileGmailOAuthURL',
  'UNIPILE_GMAIL',
);

export const getUnipileOutlookOAuthURL = oauthUrlThunk(
  'integrations/getUnipileOutlookOAuthURL',
  'UNIPILE_OUTLOOK',
);

export const getUnipileGoogleCalendarOAuthURL = oauthUrlThunk(
  'integrations/getUnipileGoogleCalendarOAuthURL',
  'UNIPILE_GOOGLE_CALENDAR',
);

export const getUnipileLinkedInOAuthURL = oauthUrlThunk(
  'integrations/getUnipileLinkedInOAuthURL',
  'UNIPILE_LINKEDIN',
);

export const getUnipileOutlookCalendarOAuthURL = oauthUrlThunk(
  'integrations/getUnipileOutlookCalendarOAuthURL',
  'UNIPILE_OUTLOOK_CALENDAR',
);

export const validateGMailCreds = simpleThunk(
  'integrations/validateGMailCreds',
  async (token, { gmailId, email, password, ...rest }) => {
    const r = await axios.post(
      '/us/integrations/connect',
      { provider: 'GMAIL_IMAP', email: email ?? gmailId, password, ...rest },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const validateGmailOAuthCallback = oauthCallbackThunk(
  'integrations/validateGmailOAuthCallback',
  'GMAIL_OAUTH',
  ({ code, state }) => ({ code, state }),
);

export const getGoogleOAuthUrl = oauthUrlThunk(
  'integrations/getGoogleOAuthUrl',
  'GOOGLE_CALENDAR',
  ({ readWriteAccess }) => ({ readWriteAccess }),
);

export const validateGoogleOAuthCallback = oauthCallbackThunk(
  'integrations/validateGoogleOAuthCallback',
  'GOOGLE_CALENDAR',
  ({ code, state, ts }) => ({ code, state, ...(ts ? { ts } : {}) }),
);

export const getOutlookOAuthURL = oauthUrlThunk(
  'integrations/getOutlookOAuthURL',
  'OUTLOOK',
);

export const getOutlookCalendarOAuthURL = oauthUrlThunk(
  'integrations/getOutlookCalendarOAuthURL',
  'OUTLOOK_CALENDAR',
);

export const validateOutlookOAuthCallback = oauthCallbackThunk(
  'integrations/validateOutlookOAuthCallback',
  'OUTLOOK',
  ({ code, state, error }) => ({ code, state, ...(error ? { error } : {}) }),
);

export const validateOutlookCalendarOAuthCallback = simpleThunk(
  'integrations/validateOutlookCalendarOAuthCallback',
  async (token, { code, state, error }) => {
    const r = await axios.post(
      '/us/integrations/oauth/callback',
      {
        provider: 'OUTLOOK_CALENDAR',
        code,
        state,
        ...(error ? { error } : {}),
      },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const getPipedriveOAuthUrl = oauthUrlThunk(
  'integrations/getPipedriveOAuthUrl',
  'PIPEDRIVE',
  ({ readWriteAccess } = {}) => ({ readWriteAccess }),
);

export const validatePipedriveOAuthCallback = oauthCallbackThunk(
  'integrations/validatePipedriveOAuthCallback',
  'PIPEDRIVE',
  ({ code, state }) => ({ code, state }),
);

export const getZohoCrmOAuthUrl = oauthUrlThunk(
  'integrations/getZohoCrmOAuthUrl',
  'ZOHO_CRM',
  ({ readWriteAccess, clientId, clientSecret }) => ({
    readWriteAccess,
    clientId,
    clientSecret,
  }),
);

export const validateZohoCrmOAuthUrl = oauthCallbackThunk(
  'integrations/validateZohoCrmOAuthUrl',
  'ZOHO_CRM',
  ({ code, state }) => ({ code, state }),
);

export const getZoomWebhookUrl = simpleThunk(
  'integrations/getZoomWebhookUrl',
  async (token) => {
    const r = await axios.get('/us/zoom/org/webhook-url', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const getZoomOAuthUrl = oauthUrlThunk(
  'integrations/getZoomOAuthUrl',
  'ZOOM',
);

export const validateZoomOAuthCallback = oauthCallbackThunk(
  'integrations/validateZoomOAuthCallback',
  'ZOOM',
  ({ code, state }) => ({ code, ...(state ? { state } : {}) }),
);

// Slack (OAuth) — provider SLACK_OAUTH. Completely independent of the legacy webhook-based
// `SLACK` integration; both can be connected on the same org at once with no cross-effects.
// See frontend_integration_guide_slack_oauth_integration.md for the full contract.
export const getSlackOAuthUrl = oauthUrlThunk(
  'integrations/getSlackOAuthUrl',
  'SLACK_OAUTH',
);

export const validateSlackOAuthCallback = oauthCallbackThunk(
  'integrations/validateSlackOAuthCallback',
  'SLACK_OAUTH',
  ({ code, state }) => ({ code, state }),
);

// Public channels + private channels the bot has been invited to. The bot must be a member
// of a channel to post into it, so anything missing here needs an `/invite` in Slack first.
// The contract is a bare array of {id, name, is_private}, but tolerate the common envelopes
// rather than silently rendering an empty dropdown when the shape drifts.
export const fetchSlackChannels = simpleThunk(
  'integrations/fetchSlackChannels',
  async (token) => {
    const r = await axios.get('/us/slack/channels', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status !== 200) {
      throw new Error(
        `Slack channels request failed (${r?.status ??
          r?.response?.status ??
          'network error'})`,
      );
    }
    const d = r.data;
    const list = [d, d?.channels, d?.data, d?.data?.channels, d?.result].find(
      Array.isArray,
    );
    if (!list) {
      throw new Error(
        `Unexpected /us/slack/channels response shape: ${JSON.stringify(
          d,
        )?.slice(0, 300)}`,
      );
    }
    // Key names have drifted before (channelId/channelName, isPrivate/private).
    return list.map((c) => ({
      id: c?.id ?? c?.channelId ?? c?.channel_id,
      name: c?.name ?? c?.channelName ?? c?.channel_name,
      is_private: c?.is_private ?? c?.isPrivate ?? c?.private ?? false,
    }));
  },
);

export const fetchSlackNotificationChannels = simpleThunk(
  'integrations/fetchSlackNotificationChannels',
  async (token) => {
    const r = await axios.get('/us/slack/notification-channels', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

// Accepts any subset of inboundEmail / artifactAnalysis / dueActionItems. Unspecified keys are
// left untouched; an empty string clears that type back to unconfigured.
export const updateSlackNotificationChannels = simpleThunk(
  'integrations/updateSlackNotificationChannels',
  async (token, payload) => {
    const r = await axios.put('/us/slack/notification-channels', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

// Local org users, each with `.custom.crmUser` set to the Slack member they're mapped to
// (auto-matched by email on connect) or null. Drives the @mention tagging settings table.
export const fetchSlackUserMapping = simpleThunk(
  'integrations/fetchSlackUserMapping',
  async (token) => {
    const r = await axios.get('/us/qai/user-mapping', {
      params: { 'request-id': uuidv4(), service: 'slack_oauth' },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const integrateHubspot = simpleThunk(
  'integrations/integrateHubspot',
  async (token, { payload, type }) => {
    const url =
      type === 'org' ? '/us/org/integrations' : '/us/users/integrations';
    const r = await axios.post(url, payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw Object.assign(new Error('Failed'), {
      responseData: r?.response?.data,
    });
  },
);

export const getOAuthCreds = simpleThunk(
  'integrations/getOAuthCreds',
  async (token) => {
    const r = await axios.get('/us/oauth-clients', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

// Reads the stored API key for one connected integration, by record id. The
// backend tries the caller's user-level integrations first, then their org's.
// Only credentials that carry an `apiKey` resolve (HubSpot private-app tokens,
// AirCall keys); OAuth-based integrations 404 here.
export const getIntegrationKey = simpleThunk(
  'integrations/getIntegrationKey',
  async (token, integrationId) => {
    if (!integrationId) throw new Error('Missing integration id');
    const r = await axios.get(`/us/integrations/${integrationId}/value`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const getSyncRequestStatus = simpleThunk(
  'integrations/getSyncRequestStatus',
  async (token, { level }) => {
    const r = await axios.get('/us/org/integrations/sync', {
      params: { 'request-id': uuidv4(), type: 'hubspot', level },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const crmHubspotSync = simpleThunk(
  'integrations/crmHubspotSync',
  async (token, dealIds) => {
    const r = await axios.post('/us/qai/deals/crm-sync', dealIds, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const syncDataWithCrm = simpleThunk(
  'integrations/syncDataWithCrm',
  async (token, { interactionId, payload }) => {
    const r = await axios.post(
      `/us/qai/deals/interaction/${interactionId}/export`,
      payload,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw Object.assign(new Error('Failed'), {
      responseData: r?.response?.data,
    });
  },
);

export const syncDataWithHubspot = simpleThunk(
  'integrations/syncDataWithHubspot',
  async (token, { salesReviewId, payload }) => {
    const r = await axios.post(
      `/cs/sale-reviews/${salesReviewId}/integrations/export`,
      payload,
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw Object.assign(new Error('Failed'), {
      responseData: r?.response?.data,
    });
  },
);

export const syncCalls = simpleThunk(
  'integrations/syncCalls',
  async (token) => {
    const r = await axios.get('/us/qai/sync/air-call', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const syncZoomCalls = simpleThunk(
  'integrations/syncZoomCalls',
  async (token) => {
    const r = await axios.get('/us/qai/sync/zoom-s2s', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const fetchOrgPreferences = simpleThunk(
  'integrations/fetchOrgPreferences',
  async (token) => {
    const r = await axios.get('/nf/org-preferences', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const updateOrgPreferences = simpleThunk(
  'integrations/updateOrgPreferences',
  async (token, payload) => {
    const r = await axios.post('/nf/org-preferences', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const connectIntegration = simpleThunk(
  'integrations/connectIntegration',
  async (token, payload) => {
    const r = await axios.post('/us/integrations/connect', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw Object.assign(new Error('Failed'), {
      responseData: r?.response?.data,
    });
  },
);

export const disconnectIntegration = simpleThunk(
  'integrations/disconnectIntegration',
  async (token, integrationId) => {
    const r = await axios.delete(`/us/integrations/${integrationId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const listUserIntegrations = simpleThunk(
  'integrations/listUserIntegrations',
  async (token, type) => {
    const r = await axios.get('/us/users/integrations', {
      params: { 'request-id': uuidv4(), ...(type ? { type } : {}) },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
  { dedupeKey: (type) => `listUserIntegrations:${type || 'all'}` },
);

export const listOrgIntegrations = simpleThunk(
  'integrations/listOrgIntegrations',
  async (token) => {
    const r = await axios.get('/us/orgs/integrations', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
  { dedupeKey: () => 'listOrgIntegrations' },
);

export const updateIntegration = simpleThunk(
  'integrations/updateIntegration',
  async (token, { integrationId, ...payload }) => {
    const r = await axios.put(`/us/integrations/${integrationId}`, payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

export const manuallyTriggerBotToJoin = simpleThunk(
  'integrations/manuallyTriggerBotToJoin',
  async (token, payload) => {
    const r = await axios.post('/us/users/meeting-bot/manual-join', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed');
  },
);

// MCP OAuth flow (Claude Desktop / other MCP clients connecting via Ory Hydra).
// See frontend-integration_v2.md for the full API contract.
export const mcpOAuthLogin = simpleThunk(
  'integrations/mcpOAuthLogin',
  async (token, { loginChallenge }) => {
    const r = await axios.post(
      '/mcp-oauth/login',
      { login_challenge: loginChallenge },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error(r?.response?.data?.error || 'Failed');
  },
);

export const getMcpOAuthConsent = simpleThunk(
  'integrations/getMcpOAuthConsent',
  async (token, { consentChallenge }) => {
    const r = await axios.get('/mcp-oauth/consent', {
      params: { 'request-id': uuidv4(), consent_challenge: consentChallenge },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error(r?.response?.data?.error || 'Failed');
  },
);

// `accessLevel` ("read" | "write") is only meaningful when decision is "allow" — it is
// omitted on deny. When omitted on an allow the backend defaults to "write" (full access),
// so the UI always sends the user's explicit choice.
export const postMcpOAuthConsent = simpleThunk(
  'integrations/postMcpOAuthConsent',
  async (token, { consentChallenge, decision, accessLevel }) => {
    const r = await axios.post(
      '/mcp-oauth/consent',
      {
        consent_challenge: consentChallenge,
        decision,
        ...(decision === 'allow' && accessLevel ? { accessLevel } : {}),
      },
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error(r?.response?.data?.error || 'Failed');
  },
);

// Not part of the documented mcp-oauth API contract yet — backend needs to add this
// endpoint. Resolves to "not connected" instead of throwing so the Integrations page
// card degrades gracefully until it exists.
export const getMcpOAuthConnectionStatus = simpleThunk(
  'integrations/getMcpOAuthConnectionStatus',
  async (token) => {
    const r = await axios.get('/mcp-oauth/status', {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    return { connected: false };
  },
);

const addM = (b, t, k) =>
  b
    .addCase(t.pending, (s) => {
      s[k].processing = true;
      s[k].done = false;
      s[k].failed = false;
    })
    .addCase(t.fulfilled, (s) => {
      s[k].processing = false;
      s[k].done = true;
      s[k].failed = false;
    })
    .addCase(t.rejected, (s, a) => {
      s[k].processing = false;
      s[k].done = false;
      s[k].failed = true;
      s[k].error = a.payload;
    });
const addF = (b, t, k) =>
  b
    .addCase(t.pending, (s) => {
      s[k].fetching = true;
      s[k].fetchingSuccessful = false;
      s[k].fetchingFailed = false;
    })
    .addCase(t.fulfilled, (s, a) => {
      s[k].fetching = false;
      s[k].fetchingSuccessful = true;
      s[k].fetchingFailed = false;
      s[k].data = a.payload;
    })
    .addCase(t.rejected, (s) => {
      s[k].fetching = false;
      s[k].fetchingSuccessful = false;
      s[k].fetchingFailed = true;
    });

const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    addF(builder, getGmailOAuthURL, 'gmailOAuthUrl');
    addF(builder, getUnipileGmailOAuthURL, 'unipileGmailOAuthUrl');
    addF(builder, getUnipileOutlookOAuthURL, 'unipileOutlookOAuthUrl');
    addF(
      builder,
      getUnipileGoogleCalendarOAuthURL,
      'unipileGoogleCalendarOAuthUrl',
    );
    addF(
      builder,
      getUnipileOutlookCalendarOAuthURL,
      'unipileOutlookCalendarOAuthUrl',
    );
    addF(builder, getUnipileLinkedInOAuthURL, 'unipileLinkedInOAuthUrl');
    addM(builder, validateGMailCreds, 'validateGMailCreds');
    addM(builder, validateGmailOAuthCallback, 'gmailOAuthCallback');
    addF(builder, getGoogleOAuthUrl, 'googleOAuthUrl');
    addM(builder, validateGoogleOAuthCallback, 'googleOAuthCallback');
    addF(builder, getOutlookOAuthURL, 'outlookOAuthUrl');
    addF(builder, getOutlookCalendarOAuthURL, 'outlookCalendarOAuthUrl');
    addM(builder, validateOutlookOAuthCallback, 'outlookOAuthCallback');
    addM(
      builder,
      validateOutlookCalendarOAuthCallback,
      'outlookCalendarOAuthCallback',
    );
    addF(builder, getPipedriveOAuthUrl, 'pipedriveOAuthUrl');
    addM(builder, validatePipedriveOAuthCallback, 'pipedriveOAuthCallback');
    addF(builder, getZohoCrmOAuthUrl, 'zohoOAuthUrl');
    addM(builder, validateZohoCrmOAuthUrl, 'zohoOAuthCallback');
    addF(builder, getZoomWebhookUrl, 'zoomWebhookUrl');
    addF(builder, getZoomOAuthUrl, 'zoomOAuthUrl');
    addM(builder, validateZoomOAuthCallback, 'zoomOAuthCallback');
    addF(builder, getSlackOAuthUrl, 'slackOAuthUrl');
    addM(builder, validateSlackOAuthCallback, 'slackOAuthCallback');
    addF(builder, fetchSlackChannels, 'slackChannels');
    addF(builder, fetchSlackNotificationChannels, 'slackNotificationChannels');
    addM(
      builder,
      updateSlackNotificationChannels,
      'updateSlackNotificationChannels',
    );
    addF(builder, fetchSlackUserMapping, 'slackUserMapping');
    addM(builder, integrateHubspot, 'integrateHubspot');
    addF(builder, getOAuthCreds, 'oAuthCreds');
    addF(builder, getIntegrationKey, 'integrationKey');
    addF(builder, getSyncRequestStatus, 'syncRequestStatus');
    addM(builder, crmHubspotSync, 'crmHubspotSync');
    addM(builder, syncDataWithCrm, 'syncDataWithCrm');
    addM(builder, syncDataWithHubspot, 'syncDataWithHubspot');
    addM(builder, syncCalls, 'syncCalls');
    addM(builder, syncZoomCalls, 'syncZoomCalls');
    addM(builder, manuallyTriggerBotToJoin, 'manuallyTriggerBotToJoin');
    addF(builder, fetchOrgPreferences, 'orgPreferences');
    addM(builder, updateOrgPreferences, 'updateOrgPreferences');
    addM(builder, connectIntegration, 'connectIntegration');
    addM(builder, disconnectIntegration, 'disconnectIntegration');
    addM(builder, updateIntegration, 'updateIntegration');
    addF(builder, listUserIntegrations, 'userIntegrations');
    addM(builder, mcpOAuthLogin, 'mcpOAuthLogin');
    addF(builder, getMcpOAuthConsent, 'mcpOAuthConsent');
    addM(builder, postMcpOAuthConsent, 'mcpOAuthConsentDecision');
    addF(builder, getMcpOAuthConnectionStatus, 'mcpOAuthConnectionStatus');
    builder
      .addCase(listOrgIntegrations.pending, (s) => {
        s.orgIntegrations.fetching = true;
        s.orgIntegrations.fetchingSuccessful = false;
        s.orgIntegrations.fetchingFailed = false;
      })
      .addCase(listOrgIntegrations.fulfilled, (s, a) => {
        s.orgIntegrations.fetching = false;
        s.orgIntegrations.fetchingSuccessful = true;
        s.orgIntegrations.fetchingFailed = false;
        s.orgIntegrations.data = a.payload;
        const map = (a.payload || []).reduce((acc, item) => {
          acc[item.provider] = item;
          return acc;
        }, {});
        s.crmConnected = map['HUBSPOT']
          ? 'hubspot'
          : map['ZOHO_CRM']
          ? 'zohocrm'
          : map['PIPEDRIVE']
          ? 'pipedrive'
          : '';
        s.aircallConnected = !!map['AIRCALL'];
        s.zoomS2SConnected = !!map['ZOOM_PRIVATE'];
      })
      .addCase(listOrgIntegrations.rejected, (s) => {
        s.orgIntegrations.fetching = false;
        s.orgIntegrations.fetchingSuccessful = false;
        s.orgIntegrations.fetchingFailed = true;
      });
  },
});

export default integrationsSlice.reducer;
